import React, { useEffect, useMemo, useState } from 'react';
import { auditLogApi, auditLogConfigApi } from '../services/api';
import { AuditLogEvent, AuditLogConfig } from '../types';
import {
  formatLogFieldsJson,
  getAuditActionLabel,
  getAuditFieldChanges,
  getAuditSubjectName,
  supportsFriendlyAuditFieldDiff,
} from '../utils/auditLogDisplay';
import { Filter, Search } from 'lucide-react';
import { formatCompanyDateTime } from '../utils/companyDateFormat';

function getConfigEntity(config: AuditLogConfig & { Entity?: string }): string {
  return String(config.entity ?? config.Entity ?? '').trim();
}

const AuditLogsReport: React.FC = () => {
  const [configs, setConfigs] = useState<AuditLogConfig[]>([]);
  const [logs, setLogs] = useState<AuditLogEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [entityFilter, setEntityFilter] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [fieldSearch, setFieldSearch] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLog, setSelectedLog] = useState<AuditLogEvent | null>(null);

  useEffect(() => {
    const loadConfigs = async () => {
      const data = await auditLogConfigApi.getAll().catch(() => []);
      const normalized = (Array.isArray(data) ? data : [])
        .map((config) => ({
          ...config,
          entity: getConfigEntity(config),
        }))
        .filter((config) => config.entity);
      setConfigs(normalized);
    };
    loadConfigs();
  }, []);

  const availableEntities = useMemo(() => {
    return Array.from(new Set(configs.map((config) => config.entity))).sort();
  }, [configs]);

  const availableActions = useMemo(() => {
    if (!entityFilter) return [];
    const config = configs.find((item) => item.entity === entityFilter);
    return config ? config.actions.slice().sort() : [];
  }, [configs, entityFilter]);

  const fetchLogs = async (pageNumber: number) => {
    setLoading(true);
    try {
      const response = await auditLogApi.getAll({
        entity: entityFilter || undefined,
        action: actionFilter || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        page: pageNumber,
        pageSize,
      });
      setLogs(response.data || []);
      setTotalPages(response.totalPages || 1);
      setPage(response.page || pageNumber);
      setHasFetched(true);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = useMemo(() => {
    if (!fieldSearch.trim()) {
      return logs;
    }
    const needle = fieldSearch.toLowerCase();
    return logs.filter((log) => {
      const fieldBlob = JSON.stringify(log.fields || []).toLowerCase();
      return (
        log.entity.toLowerCase().includes(needle) ||
        log.action.toLowerCase().includes(needle) ||
        (log.entityDisplay || '').toLowerCase().includes(needle) ||
        getAuditSubjectName(log).toLowerCase().includes(needle) ||
        (log.actorName || '').toLowerCase().includes(needle) ||
        fieldBlob.includes(needle)
      );
    });
  }, [logs, fieldSearch]);

  const selectedFieldChanges = useMemo(
    () => (selectedLog && supportsFriendlyAuditFieldDiff(selectedLog) ? getAuditFieldChanges(selectedLog) : []),
    [selectedLog]
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl md:text-2xl font-bold">Audit Logs</h2>
        <p className="text-sm md:text-base text-neutral-600">
          Review audit logs captured from configured entities and actions.
        </p>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-4 md:p-6 space-y-4">
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <Filter className="h-4 w-4" />
          Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Entity</label>
            <select
              value={entityFilter}
              onChange={(e) => {
                setEntityFilter(e.target.value);
                setActionFilter('');
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All entities</option>
              {availableEntities.map((entity) => (
                <option key={entity} value={entity}>
                  {entity}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Action</label>
            <select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(1);
              }}
              disabled={!entityFilter}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-neutral-100"
            >
              <option value="">All actions</option>
              {availableActions.map((action) => (
                <option key={action} value={action}>
                  {action}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">All Field Search</label>
            <input
              type="text"
              value={fieldSearch}
              onChange={(e) => setFieldSearch(e.target.value)}
              placeholder="Search in all fields..."
              className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => fetchLogs(1)}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Fetching...' : 'Fetch Logs'}
          </button>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-neutral-200 flex items-center gap-2 text-sm text-neutral-600">
          <Search className="h-4 w-4" />
          {loading
            ? 'Loading audit logs...'
            : hasFetched
              ? `${filteredLogs.length} records`
              : 'Set filters and click Fetch Logs'}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50">
              <tr className="border-b border-neutral-200">
                <th className="text-left p-3 font-medium text-neutral-700">Date/Time</th>
                <th className="text-left p-3 font-medium text-neutral-700">Entity</th>
                <th className="text-left p-3 font-medium text-neutral-700">Employee</th>
                <th className="text-left p-3 font-medium text-neutral-700">Action</th>
                <th className="text-left p-3 font-medium text-neutral-700">Actor</th>
                <th className="text-left p-3 font-medium text-neutral-700">Details</th>
              </tr>
            </thead>
            <tbody>
              {!hasFetched && !loading ? (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-neutral-500">
                    Choose filters and click Fetch Logs to load audit history.
                  </td>
                </tr>
              ) : filteredLogs.length === 0 && !loading ? (
                <tr>
                  <td colSpan={6} className="p-6 text-center text-neutral-500">
                    No audit logs found for the selected filters.
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="border-b border-neutral-100 last:border-b-0">
                    <td className="p-3">
                      {formatCompanyDateTime(log.occurredAt)}
                    </td>
                    <td className="p-3 font-medium">{log.entity}</td>
                    <td className="p-3">{getAuditSubjectName(log)}</td>
                    <td className="p-3">{getAuditActionLabel(log)}</td>
                    <td className="p-3">{log.actorName || log.actorId || '-'}</td>
                    <td className="p-3">
                      <button
                        onClick={() => setSelectedLog(log)}
                        className="text-blue-600 hover:text-blue-700 hover:underline text-sm"
                      >
                        View changes
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-neutral-200 flex items-center justify-between">
          <div className="text-sm text-neutral-600">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchLogs(Math.max(1, page - 1))}
              disabled={page <= 1 || loading || !hasFetched}
              className="px-3 py-1 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => fetchLogs(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages || loading || !hasFetched}
              className="px-3 py-1 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl shadow-lg max-h-[90vh] flex flex-col">
            <div className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
              <div className="font-semibold text-neutral-900">
                {getAuditActionLabel(selectedLog)}
                {getAuditSubjectName(selectedLog) !== '-'
                  ? ` — ${getAuditSubjectName(selectedLog)}`
                  : ''}
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-neutral-600 hover:text-neutral-900"
              >
                Close
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0">
              <div className="text-sm text-neutral-600">
                Changed by {selectedLog.actorName || selectedLog.actorId || 'Unknown'} on{' '}
                {formatCompanyDateTime(selectedLog.occurredAt)}
              </div>

              {supportsFriendlyAuditFieldDiff(selectedLog) ? (
                selectedFieldChanges.length > 0 ? (
                  <div className="border border-neutral-200 rounded-md overflow-hidden max-h-[55vh] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50">
                        <tr className="border-b border-neutral-200">
                          <th className="text-left p-3 font-medium text-neutral-700">Field</th>
                          <th className="text-left p-3 font-medium text-neutral-700">Previous value</th>
                          <th className="text-left p-3 font-medium text-neutral-700">New value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedFieldChanges.map((change) => (
                          <tr key={change.fieldKey} className="border-b border-neutral-100 last:border-b-0">
                            <td className="p-3 font-medium text-neutral-800">{change.label}</td>
                            <td className="p-3 text-neutral-700">{change.oldValue}</td>
                            <td className="p-3 text-neutral-700">{change.newValue}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm text-neutral-500 border border-neutral-200 rounded-md p-4 bg-neutral-50">
                    No meaningful field changes were recorded for this update.
                  </div>
                )
              ) : (
                <div className="border border-neutral-200 rounded-md bg-neutral-50 p-4 max-h-[60vh] overflow-auto">
                  <pre className="text-xs text-neutral-800 whitespace-pre-wrap break-words font-mono leading-5">
                    {formatLogFieldsJson(selectedLog)}
                  </pre>
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-neutral-200 flex justify-end">
              <button
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 border border-neutral-300 rounded-md hover:bg-neutral-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogsReport;
